import type FullCalendar from '@fullcalendar/react';
import { type FunctionComponent, type ReactNode, type RefObject } from 'react';
import { createContext } from 'use-context-selector';

import type { ViewId } from './Planner.types';
import type { UpsellCohort } from './useUpsellData';

export interface PlannerContextValue {
  calendarRef: RefObject<FullCalendar | null>;
  plannerWrapperRef: RefObject<HTMLDivElement>;
  upsellCohort: UpsellCohort;
  currentView: ViewId;
}

export const emptyPlannerContext: PlannerContextValue = {
  calendarRef: { current: null },
  plannerWrapperRef: { current: null },
  upsellCohort: 'paidUser',
  currentView: 'timeGridWeek',
};

export const PlannerContext =
  createContext<PlannerContextValue>(emptyPlannerContext);

/**
 * A bit hard to get this right: the FullCalendar component allows us to plug in
 * a ref object to gain access to the underlying FullCalendar instance.
 * This instance is the sanctioned way to access the FullCalendar API, but it's
 * a little awkward because we want to access this API in sibling components of
 * the calendar itself, like the header and width observer.
 *
 * This context provider is connected to the FullCalendar component and should
 * wrap it along with any sibling elements that also need access to the API.
 * However, because of render order, we cannot guarantee that the FullCalendar
 * instance is available on initial render; as a workaround, we _persist_ the
 * instance as a ref object, so consumers can continue to access it on demand.
 *
 * Note that the `getApi()` method simply returns an instance property, so it's
 * extremely lightweight, and consumers need not be concerned about calling it.
 *
 * @example
 * ```ts
 * const { calendarRef } = useContext(PlannerContext);
 *
 * const doSomethingWithCalendarApi = useCallback(() => {
 *   const calendarApi = calendarRef.current?.getApi();
 *   calendarApi?.doSomething();
 * }, [calendarRef]);
 * ```
 */
export const PlannerContextProvider: FunctionComponent<{
  calendarRef: RefObject<FullCalendar | null>;
  plannerWrapperRef: RefObject<HTMLDivElement>;
  upsellCohort: UpsellCohort;
  children: ReactNode;
}> = ({ calendarRef, plannerWrapperRef, upsellCohort, children }) => {
  // eslint-disable-next-line @eslint-react/no-unstable-context-value
  const value = {
    calendarRef,
    currentView:
      (calendarRef.current?.getApi()?.view.type as ViewId) || 'timeGridWeek',
    plannerWrapperRef,
    upsellCohort,
  };

  return (
    <PlannerContext.Provider value={value}>{children}</PlannerContext.Provider>
  );
};
